import React, { useEffect, useState } from "react";
import {
  changeSuperAdminCompanyPackage,
  deleteSuperAdminCompany,
  getSuperAdminCompany,
  getSuperAdminCompanies,
  getSuperAdminCompanyPaymentMethods,
  getSuperAdminDashboard,
  getSuperAdminPackages,
  getSuperAdminSettings,
  loginAsVendor,
  SuperAdminCurrencyOption,
  SuperAdminLanguageOption,
  SuperAdminOfflinePaymentMethod,
  PaginatedResponse,
  SuperAdminCompany,
  SuperAdminDashboardData,
  SuperAdminPackage,
  scrapeSuperAdminCompany,
  importSuperAdminCompanyCustomers,
  SUPERADMIN_CUSTOMERS_CSV_SAMPLE_URL,
  updateSuperAdminCompany,
} from "../../services/superAdminApi";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { CreditCard, Eye, FileText, KeyRound, Link as LinkIcon, Pencil, RefreshCcw, Trash2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatBillingAmount, formatBillingCycle, formatSubscriptionStatus } from "../../services/billing";

const cardCls = "bg-white rounded-xl shadow-sm border border-gray-200 p-4";

const formatDate = (date: string) => {
  if (!date) return "-";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusBadge = (status?: string) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "active") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
        Active
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
      Inactive
    </span>
  );
};

const verifiedBadge = (verified?: string) => {
  const isVerified = (verified || "").toLowerCase() === "yes";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
        isVerified
          ? "bg-green-100 text-green-700"
          : "bg-amber-100 text-amber-700"
      }`}
    >
      {isVerified ? "Yes" : "No"}
    </span>
  );
};

const modalOverlayCls = "fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4";
const modalCls = "w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-gray-200";

const formatInputDate = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const addDateByPackageType = (payDate: string, packageType: string) => {
  if (!payDate) return "";
  const d = new Date(payDate);
  if (Number.isNaN(d.getTime())) return "";
  if (packageType === "monthly") {
    d.setMonth(d.getMonth() + 1);
  } else {
    d.setFullYear(d.getFullYear() + 1);
  }
  return d.toISOString().slice(0, 10);
};

const paidBadge = (hasPaid?: boolean) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
      hasPaid ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {hasPaid ? "Yes" : "No"}
  </span>
);

const subscriptionStatusBadge = (status?: string | null) => {
  const raw = String(status || "").toLowerCase();
  if (!raw) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
        No subscription
      </span>
    );
  }
  if (raw === "active") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
        Active
      </span>
    );
  }
  if (raw === "past_due") {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">
        Past due
      </span>
    );
  }
  if (raw === "pending_setup" || raw === "pending") {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
        Pending setup
      </span>
    );
  }
  if (raw === "cancelled" || raw === "canceled") {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
        Cancelled
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
      {formatSubscriptionStatus(status)}
    </span>
  );
};

const getSubscriptionIconClass = (status?: string | null) => {
  const raw = String(status || "").toLowerCase();
  if (raw === "active") return "bg-green-100 text-green-700 hover:bg-green-200";
  if (raw === "past_due") return "bg-amber-100 text-amber-700 hover:bg-amber-200";
  if (raw === "pending_setup" || raw === "pending") {
    return "bg-blue-100 text-blue-700 hover:bg-blue-200";
  }
  return "bg-gray-100 text-gray-500 hover:bg-gray-200";
};

const getSubscriptionIconTitle = (status?: string | null) => {
  const raw = String(status || "").toLowerCase();
  if (!raw) return "View subscription billing";
  return `Subscription: ${formatSubscriptionStatus(status)}`;
};

const SuperAdminCompanies: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SuperAdminCompany[]>([]);
  const [dashboardMeta, setDashboardMeta] = useState<SuperAdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [verified, setVerified] = useState("");
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginatedResponse<SuperAdminCompany> | null>(null);
  const [packages, setPackages] = useState<SuperAdminPackage[]>([]);
  const [currencies, setCurrencies] = useState<SuperAdminCurrencyOption[]>([]);
  const [languages, setLanguages] = useState<SuperAdminLanguageOption[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<SuperAdminOfflinePaymentMethod[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<SuperAdminCompany | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [packageOpen, setPackageOpen] = useState(false);
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingCompany, setBillingCompany] = useState<SuperAdminCompany | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [booksyLink, setBooksyLink] = useState("");
  const [editLogoFile, setEditLogoFile] = useState<File | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [packageForm, setPackageForm] = useState<Record<string, any>>({
    package_id: "",
    package_type: "annual",
    amount: "",
    pay_date: "",
    licence_expire_on: "",
    payment_method_id: "",
  });

  const fetchCompanies = async (
    targetPage = page,
    overrides?: { search?: string; status?: string; verified?: string; subscription_status?: string }
  ) => {
    const effectiveSearch = overrides?.search ?? search;
    const effectiveStatus = overrides?.status ?? status;
    const effectiveVerified = overrides?.verified ?? verified;
    const effectiveSubscriptionStatus = overrides?.subscription_status ?? subscriptionStatus;

    try {
      setLoading(true);
      setError("");
      const res = await getSuperAdminCompanies({
        per_page: perPage,
        page: targetPage,
        search: effectiveSearch || undefined,
        status: effectiveStatus || undefined,
        verified: effectiveVerified || undefined,
        subscription_status: effectiveSubscriptionStatus || undefined,
      });

      setRows(res.data || []);
      setPagination(res);
      setPage(res.current_page || targetPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load companies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const dashboard = await getSuperAdminDashboard();
        setDashboardMeta(dashboard);
        const packagesResponse = await getSuperAdminPackages({ per_page: 100 });
        setPackages(packagesResponse.data || []);
        const settingsResponse = await getSuperAdminSettings();
        setCurrencies(settingsResponse?.currencies || []);
        setLanguages(settingsResponse?.languages || []);
        const methodsResponse = await getSuperAdminCompanyPaymentMethods();
        setPaymentMethods(methodsResponse || []);
      } catch (_e) {
        // Keep listing usable even if meta cards fail.
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    fetchCompanies(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  const handleApplyFilters = () => {
    fetchCompanies(1);
  };

  const handleResetFilters = () => {
    setSearch("");
    setStatus("");
    setSubscriptionStatus("");
    setVerified("");
    fetchCompanies(1, { search: "", status: "", verified: "", subscription_status: "" });
  };

  const total = pagination?.total ?? rows.length;
  const currentPage = pagination?.current_page ?? 1;
  const lastPage = pagination?.last_page ?? 1;

  const openBillingModal = async (row: SuperAdminCompany) => {
    setBillingCompany(row);
    setBillingOpen(true);

    try {
      const company = await getSuperAdminCompany(row.id);
      setBillingCompany(company);
    } catch (_e) {
      // Keep list row billing data if refresh fails.
    }
  };

  const openCompanyDetails = async (companyId: number, mode: "view" | "edit" | "package" | "scrape") => {
    try {
      setSubmitting(true);
      const company = await getSuperAdminCompany(companyId);
      setSelectedCompany(company);
      setEditForm({
        company_name: company.company_name || "",
        company_email: company.company_email || "",
        company_phone: company.company_phone || "",
        xecrm_support_id: company.xecrm_support_id || "",
        xecrm_company_id: company.xecrm_company_id || "",
        status: company.status || "active",
        verified: company.verified || "yes",
        website: company.website || "",
        address: company.address || "",
        timezone: company.timezone || "",
        currency_id: company.currency?.id || "",
        locale: company.locale || "",
        date_format: company.date_format || "d-m-Y",
        time_format: company.time_format || "h:i A",
        website_status: company.website_status || "active",
      });
      setEditLogoFile(null);
      const selectedPackage = packages.find((pkg) => pkg.id === company.package_id);
      const defaultPayDate = new Date().toISOString().slice(0, 10);
      const defaultPackageType = "annual";
      setPackageForm({
        package_id: company.package_id || "",
        package_type: defaultPackageType,
        amount: String(selectedPackage?.annual_price ?? ""),
        pay_date: defaultPayDate,
        licence_expire_on:
          formatInputDate(company.licence_expire_on || company.trial_ends_at) ||
          addDateByPackageType(defaultPayDate, defaultPackageType),
        payment_method_id: paymentMethods[0]?.id || "",
      });
      setBooksyLink("");
      setDetailOpen(mode === "view");
      setEditOpen(mode === "edit");
      setPackageOpen(mode === "package");
      setScrapeOpen(mode === "scrape");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load company details.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginAsVendor = async (companyId: number) => {
    try {
      const payload = await loginAsVendor(companyId);
      const token = payload?.access_token;
      const user = payload?.user;

      if (!token || !user) {
        throw new Error("Invalid vendor login response.");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify({ ...user, roles: payload?.roles || user?.roles || [] }));
      navigate("/dashboard", { replace: true });
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to login as vendor.");
    }
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;
    try {
      setSubmitting(true);
      const payload = new FormData();
      Object.entries(editForm).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          payload.append(key, String(value));
        }
      });
      if (editLogoFile) {
        payload.append("logo", editLogoFile);
      }

      await updateSuperAdminCompany(selectedCompany.id, payload as any);
      setEditOpen(false);
      await fetchCompanies(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to update company.");
    } finally {
      setSubmitting(false);
    }
  };

  const updatePackagePricing = (packageId: number, packageType: string) => {
    const selectedPackage = packages.find((pkg) => pkg.id === packageId);
    if (!selectedPackage) return "";
    return packageType === "monthly"
      ? String(selectedPackage.monthly_price ?? "")
      : String(selectedPackage.annual_price ?? "");
  };

  const handlePackageIdChange = (value: string) => {
    const packageId = Number(value);
    const amount = updatePackagePricing(packageId, packageForm.package_type || "annual");
    setPackageForm((p: Record<string, any>) => ({ ...p, package_id: value, amount }));
  };

  const handlePackageTypeChange = (value: string) => {
    const nextAmount = updatePackagePricing(Number(packageForm.package_id), value);
    const nextLicenceDate = addDateByPackageType(packageForm.pay_date, value);
    setPackageForm((p: Record<string, any>) => ({
      ...p,
      package_type: value,
      amount: nextAmount,
      licence_expire_on: nextLicenceDate || p.licence_expire_on,
    }));
  };

  const handlePayDateChange = (value: string) => {
    const nextLicenceDate = addDateByPackageType(value, packageForm.package_type || "annual");
    setPackageForm((p: Record<string, any>) => ({
      ...p,
      pay_date: value,
      licence_expire_on: nextLicenceDate || p.licence_expire_on,
    }));
  };

  const handleChangePackage = async () => {
    if (
      !selectedCompany ||
      !packageForm.package_id ||
      !packageForm.package_type ||
      !packageForm.amount ||
      !packageForm.pay_date ||
      !packageForm.licence_expire_on
    ) return;
    try {
      setSubmitting(true);
      await changeSuperAdminCompanyPackage({
        company_id: selectedCompany.id,
        package_id: Number(packageForm.package_id),
        package_type: packageForm.package_type,
        amount: Number(packageForm.amount),
        pay_date: packageForm.pay_date,
        licence_expire_on: packageForm.licence_expire_on,
        payment_method_id: packageForm.payment_method_id
          ? Number(packageForm.payment_method_id)
          : undefined,
      });
      setPackageOpen(false);
      await fetchCompanies(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to change package.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScrape = async () => {
    if (!selectedCompany || !booksyLink) return;
    try {
      setSubmitting(true);
      await scrapeSuperAdminCompany({ company_id: selectedCompany.id, booksy_link: booksyLink });
      setScrapeOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to scrape company services.");
    } finally {
      setSubmitting(false);
    }
  };

  const openImportCustomers = (row: SuperAdminCompany) => {
    setSelectedCompany(row);
    setImportFile(null);
    setImportResult("");
    setError("");
    setImportOpen(true);
  };

  const handleImportCustomers = async () => {
    if (!selectedCompany || !importFile) return;
    try {
      setSubmitting(true);
      setError("");
      setImportResult("");
      const result = await importSuperAdminCompanyCustomers(selectedCompany.id, importFile);
      const msg = `Imported ${result.imported} customer(s).${
        result.duplicates > 0 ? ` ${result.duplicates} duplicate email(s) skipped.` : ""
      }`;
      setImportResult(msg);
      toast.success(msg);
      setImportFile(null);
    } catch (e: any) {
      const message = e?.response?.data?.message || "Failed to import customers.";
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    try {
      await deleteSuperAdminCompany(selectedCompany.id);
      setDeleteOpen(false);
      setSelectedCompany(null);
      await fetchCompanies(currentPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to delete company.");
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Companies</h1>
        <p className="text-sm text-gray-500">
          Manage all tenant companies with the same core actions as the web version.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Total Companies</p>
          <p className="text-2xl font-bold text-[#402B69]">{dashboardMeta?.companies.total ?? total}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Active Companies</p>
          <p className="text-2xl font-bold text-green-600">{dashboardMeta?.companies.active ?? 0}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Inactive Companies</p>
          <p className="text-2xl font-bold text-red-600">{dashboardMeta?.companies.inactive ?? 0}</p>
        </div>
        <div className={cardCls}>
          <p className="text-xs text-gray-500">Unverified Companies</p>
          <p className="text-2xl font-bold text-amber-600">{dashboardMeta?.companies.unverified ?? 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="min-w-[220px]">
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Company name, email, subdomain..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deactive">Deactive</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Subscription</label>
          <select
            value={subscriptionStatus}
            onChange={(e) => setSubscriptionStatus(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="pending_setup">Pending setup</option>
            <option value="past_due">Past due</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Verified</label>
          <select
            value={verified}
            onChange={(e) => setVerified(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows per page</label>
          <select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <button
          onClick={handleApplyFilters}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Apply
        </button>
        <button
          onClick={handleResetFilters}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold"
        >
          Reset
        </button>
        <div className="ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate("/superadmin/companies/create")}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Create New
          </button>
          <a
            href="https://salonify.co.uk/super-admin/companies/export"
            target="_blank"
            rel="noreferrer"
            className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Export
          </a>
          <button
            type="button"
            onClick={() => navigate("/superadmin/companies/claimed-businesses")}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Claimed Businesses
          </button>
          <button
            type="button"
            onClick={() => navigate("/superadmin/companies/claims")}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            View Claims
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-600">Loading companies...</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Company</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Logo</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Package</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Verified</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td className="px-4 py-6 text-gray-500" colSpan={9}>
                        No companies found for selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-700">{row.id}</td>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="font-medium text-[#402B69]">{row.company_name}</div>
                          <div className="text-xs text-gray-500">
                            {row.subdomain || row.custom_domain || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div>{row.company_email || "-"}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {row.logo_url ? (
                            <img src={row.logo_url} alt={row.company_name} className="h-10 w-10 rounded-lg object-cover border border-gray-200" />
                          ) : (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div>{row.billing?.package_name || row.package?.name || "No package"}</div>
                          <button
                            type="button"
                            onClick={() => openCompanyDetails(row.id, "package")}
                            className="mt-1 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
                          >
                            Change
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{statusBadge(row.status)}</td>
                        <td className="px-4 py-3 text-gray-700">{verifiedBadge(row.verified)}</td>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openBillingModal(row)}
                              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${getSubscriptionIconClass(row.billing?.subscription_status)}`}
                              title={getSubscriptionIconTitle(row.billing?.subscription_status)}
                            >
                              <CreditCard size={15} />
                            </button>
                            {row.is_claim === "no" && row.slug ? (
                              <a
                                href={`https://booking.getglossa.com/business/${row.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 rounded-lg bg-sky-100 px-2.5 py-1.5 text-xs font-semibold text-sky-700"
                              >
                                Claim URL
                              </a>
                            ) : null}
                            {row.slug ? (
                              <a
                                href={`/vendor/${row.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700"
                                title="Vendor Link"
                              >
                                <LinkIcon size={15} />
                              </a>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => handleLoginAsVendor(row.id)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-900 text-white"
                              title="Login as Vendor"
                            >
                              <KeyRound size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openCompanyDetails(row.id, "scrape")}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700"
                              title="Scrap Form"
                            >
                              <FileText size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openImportCustomers(row)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700"
                              title="Import Customers CSV"
                            >
                              <Upload size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openCompanyDetails(row.id, "view")}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 text-sky-700"
                              title="View Details"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(`/superadmin/companies/${row.id}/edit`)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700"
                              title="Edit Company"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCompany(row);
                                setDeleteOpen(true);
                              }}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-700"
                              title="Delete Company"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                Showing page {currentPage} of {lastPage} ({total} total companies)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchCompanies(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchCompanies(currentPage + 1)}
                  disabled={currentPage >= lastPage || loading}
                  className="px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {detailOpen && selectedCompany ? (
        <div className={modalOverlayCls} onClick={() => setDetailOpen(false)}>
          <div className={modalCls} onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-[#402B69]">Company Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 text-sm">
              <div><span className="font-semibold">Company Name:</span> {selectedCompany.company_name}</div>
              <div><span className="font-semibold">Company Email:</span> {selectedCompany.company_email}</div>
              <div><span className="font-semibold">Company Phone:</span> {selectedCompany.company_phone}</div>
              <div><span className="font-semibold">Website:</span> {selectedCompany.website || "-"}</div>
              <div><span className="font-semibold">Address:</span> {selectedCompany.address || "-"}</div>
              <div><span className="font-semibold">Currency:</span> {selectedCompany.currency?.currency_symbol} {selectedCompany.currency?.currency_code}</div>
              <div><span className="font-semibold">Timezone:</span> {selectedCompany.timezone || "-"}</div>
              <div><span className="font-semibold">Status:</span> {selectedCompany.status}</div>
              <div><span className="font-semibold">Package:</span> {selectedCompany.package?.name || "-"}</div>
              <div><span className="font-semibold">Employees Quota:</span> {(selectedCompany.user_count ?? 0)} / {(selectedCompany.package?.max_employees ?? "-")}</div>
              <div><span className="font-semibold">Bookings:</span> {selectedCompany.bookings_count ?? 0}</div>
              <div><span className="font-semibold">Services:</span> {selectedCompany.services_count ?? 0}</div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <button onClick={() => setDetailOpen(false)} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">Close</button>
              <button
                onClick={() => {
                  setDetailOpen(false);
                  navigate(`/superadmin/companies/${selectedCompany.id}/edit`);
                }}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Edit Company
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {billingOpen && billingCompany ? (
        <div className={modalOverlayCls} onClick={() => setBillingOpen(false)}>
          <div className={modalCls} onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#402B69]">Subscription Billing</h3>
                <p className="text-sm text-gray-500 mt-1">{billingCompany.company_name}</p>
              </div>
              {subscriptionStatusBadge(billingCompany.billing?.subscription_status)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 text-sm">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Plan</p>
                <p className="mt-1 text-base font-semibold text-[#402B69]">
                  {billingCompany.billing?.package_name || billingCompany.package?.name || "No package"}
                </p>
                <p className="mt-1 text-gray-500">
                  {formatBillingCycle(billingCompany.billing?.billing_cycle || "")}
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Amount</p>
                <p className="mt-1 text-base font-semibold text-[#885ABB]">
                  {billingCompany.billing?.amount_cents != null
                    ? formatBillingAmount(
                        billingCompany.billing.amount_cents,
                        billingCompany.billing.currency,
                      )
                    : "-"}
                </p>
                <p className="mt-1 text-gray-500">
                  {billingCompany.billing?.billing_cycle === "annual" ||
                  billingCompany.billing?.billing_cycle === "yearly"
                    ? "Per year"
                    : "Per month"}
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Payment received</p>
                <div className="mt-2">{paidBadge(billingCompany.billing?.has_paid)}</div>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Card on file</p>
                <p className="mt-2 font-medium text-gray-800">
                  {billingCompany.billing?.payment_method?.last4
                    ? `${billingCompany.billing.payment_method.brand || "Card"} •••• ${billingCompany.billing.payment_method.last4}`
                    : "No card saved"}
                </p>
              </div>

              <div>
                <span className="font-semibold text-gray-700">Next billing date:</span>{" "}
                {formatDate(billingCompany.billing?.next_charge_at || "")}
              </div>
              <div>
                <span className="font-semibold text-gray-700">Last payment:</span>{" "}
                {formatDate(billingCompany.billing?.last_payment_at || "")}
              </div>
              {billingCompany.billing?.last_payment_amount_cents != null ? (
                <div>
                  <span className="font-semibold text-gray-700">Last payment amount:</span>{" "}
                  {formatBillingAmount(
                    billingCompany.billing.last_payment_amount_cents,
                    billingCompany.billing.currency,
                  )}
                </div>
              ) : null}
              <div>
                <span className="font-semibold text-gray-700">Company status:</span>{" "}
                {statusBadge(billingCompany.status)}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <button
                onClick={() => setBillingOpen(false)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setBillingOpen(false);
                  openCompanyDetails(billingCompany.id, "package");
                }}
                className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Change package
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editOpen && selectedCompany ? (
        <div className={modalOverlayCls} onClick={() => setEditOpen(false)}>
          <div className={modalCls} onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-[#402B69]">Edit Company</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 text-sm">
              <input value={editForm.company_name || ""} onChange={(e) => setEditForm((p) => ({ ...p, company_name: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Company Name" />
              <input value={editForm.company_email || ""} onChange={(e) => setEditForm((p) => ({ ...p, company_email: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Company Email" />
              <input value={editForm.company_phone || ""} onChange={(e) => setEditForm((p) => ({ ...p, company_phone: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Company Phone" />
              <input type="file" accept=".png,.jpg,.jpeg" onChange={(e) => setEditLogoFile(e.target.files?.[0] || null)} className="border rounded-lg px-3 py-2" />
              <input value={editForm.website || ""} onChange={(e) => setEditForm((p) => ({ ...p, website: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Website" />
              <input value={editForm.address || ""} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Address" />
              <input value={editForm.timezone || ""} onChange={(e) => setEditForm((p) => ({ ...p, timezone: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Timezone" />
              <select value={editForm.currency_id || ""} onChange={(e) => setEditForm((p) => ({ ...p, currency_id: e.target.value }))} className="border rounded-lg px-3 py-2">
                <option value="">Select currency</option>
                {currencies.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.currency_symbol} ({currency.currency_code})
                  </option>
                ))}
              </select>
              <select value={editForm.locale || ""} onChange={(e) => setEditForm((p) => ({ ...p, locale: e.target.value }))} className="border rounded-lg px-3 py-2">
                <option value="">Select language</option>
                {languages.map((language) => (
                  <option key={language.id} value={language.language_code || ""}>
                    {language.language_name || language.language_code}
                  </option>
                ))}
              </select>
              <input value={editForm.date_format || ""} onChange={(e) => setEditForm((p) => ({ ...p, date_format: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Date Format" />
              <input value={editForm.time_format || ""} onChange={(e) => setEditForm((p) => ({ ...p, time_format: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="Time Format" />
              <select value={editForm.website_status || "active"} onChange={(e) => setEditForm((p) => ({ ...p, website_status: e.target.value }))} className="border rounded-lg px-3 py-2">
                <option value="active">Website Active</option>
                <option value="inactive">Website Inactive</option>
              </select>
              <select value={editForm.status || ""} onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))} className="border rounded-lg px-3 py-2">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                <input value={editForm.xecrm_support_id || ""} onChange={(e) => setEditForm((p) => ({ ...p, xecrm_support_id: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="XECRM Support ID" />
                <input value={editForm.xecrm_company_id || ""} onChange={(e) => setEditForm((p) => ({ ...p, xecrm_company_id: e.target.value }))} className="border rounded-lg px-3 py-2" placeholder="XECRM Company ID" />
                <select value={editForm.verified || ""} onChange={(e) => setEditForm((p) => ({ ...p, verified: e.target.value }))} className="border rounded-lg px-3 py-2">
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <button onClick={() => setEditOpen(false)} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
              <button onClick={handleUpdateCompany} disabled={submitting} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">{submitting ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {packageOpen && selectedCompany ? (
        <div className={modalOverlayCls} onClick={() => setPackageOpen(false)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-[#E5DDF5]" onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#402B69]">Change Package</h3>
              <button
                onClick={() => setPackageOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 text-sm">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Business Name</label>
                <input
                  value={selectedCompany.company_name || ""}
                  disabled
                  className="w-full rounded-md border border-[#D5C6EE] bg-[#F7F3FF] px-3 py-2 text-sm text-[#402B69]"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Package</label>
                <select
                  value={packageForm.package_id || ""}
                  onChange={(e) => handlePackageIdChange(e.target.value)}
                  className="w-full rounded-md border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]"
                >
                  <option value="">Select package</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Package Type</label>
                  <select
                    value={packageForm.package_type || "annual"}
                    onChange={(e) => handlePackageTypeChange(e.target.value)}
                    className="w-full rounded-md border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Amount</label>
                  <input
                    type="number"
                    min="0"
                    value={packageForm.amount || ""}
                    onChange={(e) => setPackageForm((p: Record<string, any>) => ({ ...p, amount: e.target.value }))}
                    className="w-full rounded-md border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Pay Date</label>
                  <input
                    type="date"
                    value={packageForm.pay_date || ""}
                    onChange={(e) => handlePayDateChange(e.target.value)}
                    className="w-full rounded-md border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Licence expires On</label>
                  <input
                    type="date"
                    value={packageForm.licence_expire_on || ""}
                    onChange={(e) => setPackageForm((p: Record<string, any>) => ({ ...p, licence_expire_on: e.target.value }))}
                    className="w-full rounded-md border border-[#D5C6EE] bg-[#F7F3FF] px-3 py-2 text-sm text-[#402B69]"
                    readOnly
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Choose Payment Method{" "}
                  <a
                    href="https://salonify.co.uk/super-admin/payment-settings#offline"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded bg-[#885ABB] px-2 py-0.5 text-xs font-semibold text-white ml-2 hover:bg-[#7748A4]"
                  >
                    Add New
                  </a>
                </label>
                <select
                  value={packageForm.payment_method_id || ""}
                  onChange={(e) => setPackageForm((p: Record<string, any>) => ({ ...p, payment_method_id: e.target.value }))}
                  className="w-full rounded-md border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]"
                >
                  <option value="">Select payment method</option>
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>{method.name.toUpperCase()}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <button onClick={() => setPackageOpen(false)} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleChangePackage}
                disabled={
                  submitting ||
                  !packageForm.package_id ||
                  !packageForm.package_type ||
                  !packageForm.amount ||
                  !packageForm.pay_date ||
                  !packageForm.licence_expire_on
                }
                className="rounded-lg bg-[#885ABB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7748A4] disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {scrapeOpen && selectedCompany ? (
        <div className={modalOverlayCls} onClick={() => setScrapeOpen(false)}>
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-[#402B69]">Company Services Scrap Form</h3>
            </div>
            <div className="p-6 space-y-4">
              <h4 className="text-xl font-semibold text-sky-600">{selectedCompany.company_name}</h4>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Booksy Link</label>
                <input
                  type="url"
                  value={booksyLink}
                  onChange={(e) => setBooksyLink(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Enter Booksy link"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <button onClick={() => setScrapeOpen(false)} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
              <button onClick={handleScrape} disabled={submitting || !booksyLink} className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white">{submitting ? "Submitting..." : "Submit"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {importOpen && selectedCompany ? (
        <div className={modalOverlayCls} onClick={() => setImportOpen(false)}>
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-[#402B69]">Import Customers</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedCompany.company_name}</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a CSV to add customers to this company. Duplicate emails are skipped.
                Default password for imported users is <span className="font-semibold">123456</span>.
              </p>
              <a
                href={SUPERADMIN_CUSTOMERS_CSV_SAMPLE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-lg border border-[#D5C6EE] bg-[#F7F3FF] px-3 py-2 text-sm font-semibold text-[#885ABB] hover:bg-[#EFE6FF]"
              >
                Download sample CSV
              </a>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Customers CSV *</label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm"
                />
                {importFile ? (
                  <p className="mt-2 text-xs text-gray-500">
                    Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                  </p>
                ) : null}
              </div>
              {importResult ? (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {importResult}
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <button
                type="button"
                onClick={() => setImportOpen(false)}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleImportCustomers}
                disabled={submitting || !importFile}
                className="rounded-lg bg-[#885ABB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7748A4] disabled:opacity-50"
              >
                {submitting ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteCompany}
        title="Delete Company"
        message={`Are you sure you want to delete ${selectedCompany?.company_name || "this company"}?`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default SuperAdminCompanies;

